import { useContext, useEffect, useRef, useState } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { AuthContext } from "../providers/AuthContextProvider";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { Button } from "../components/Shared/Buttons/Button";
import { SingleSelect } from "../components/Shared/SingleSelect";
import { SelectInputs } from "../models/SelectInputs";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Excel } from "../assets/svg/excel.svg";
import { PieChartComponet } from "../components/Shared/PieChartComponet";
import { useMediaQuery } from "react-responsive";
import useErrorPage from "../hooks/useErrorPage";
import { StatSearchCriteria } from "../models/StatSearchCriteria";
import { downloadStatToExcel, getOrganizationDetails, getStatByDate, getStatByJob, getStatByStatus } from "../services/RecruiterService";
import { StatByJobModel } from "../models/StatByJobModel";
import { StatByDateModel } from "../models/StatByDateModel";
import { ApplicationStatus } from "../models/ApplicationStatus";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { VacancyListLoader } from "../components/Loaders/VacancyListLoader";
import { FeedbackCard } from "../components/Shared/FeedbackCard";
import { ReactComponent as Calendar } from "../assets/svg/calendar.svg";
import { formatDateWithSuperscript } from "../helpers/Common";
import { Pagination } from "../components/Shared/Pagination";
import { Footer } from "../components/Shared/Footer";
import { StatisticsLoader } from "../components/Loaders/StatisticsLoader";
import { VacancyCountLoader } from "../components/Loaders/VacancyCountLoader";
import { VacancyModel } from "../models/VacancyDetails";
import { Option } from "../models/SelectInputs";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { StatsCountModel } from "../models/StatsCountModel";
import { Color } from "../enums/Color";
import { UserType } from "../enums/UserType";

const formatDate = (date: Date) => new Intl.DateTimeFormat('en-CA').format(date);

const initialStatsCount: StatsCountModel = {
    totalJobCount: 0,
    cvLessJobCount: 0,
    cvBasedJobCount: 0,
    totalApplicationCount: 0,
    openedCount: 0,
    notOpenedCount: 0,
    openedRate: 0,
    notOpenedRate: 0,
    openedFilteredCount: 0,
    notOpenedFilteredCount: 0,
    openedFilteredRate: 0,
    notOpenedFilteredRate: 0
}

export const StatisticsPage: React.FC = () => {
    const navigate = useNavigate();     
    const authContext = useContext(AuthContext);        
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const styleContext = useContext(StyleContext);
    const styles = styleContext.getComponentStyle("statisticsPage"); 
    const [orgId, setOrgId] = useState<number>(recruiter?.organizationId || 0);
    const [orgName, setOrgName] = useState<string>();
    const [pageSize, setPageSize] = useState<number>(20);
    const [page, setPage] = useState<number>(1);    
    const [jobType, setJobType] = useState<string>("All");
    const [jobList, setJobList] = useState<StatByJobModel[]>([]);
    const [paginatedJobs, setPaginatedJobs] = useState<StatByJobModel[]>([]);
    const [selectedJob, setSelectedJob] = useState<StatByJobModel | "All">("All");
    const [jobStats, setJobStats] = useState<StatByJobModel[]>([]);
    const [dateStats, setDateStats ] = useState<StatByDateModel[]>([]);
    const [statusStats, setStatusStats] = useState<ApplicationStatus[]>([]);
    const [statsCount, setStatsCount] = useState<StatsCountModel>(initialStatsCount);    
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());  
    const [startDate, setStartDate] = useState<string>(formatDate(oneMonthAgo));
    const [endDate, setEndDate] = useState<string>(formatDate(today));
    const [jobStatLoading, setJobStatLoading] = useState<boolean>(true);
    const [hasSubOrganizations, setHasSubOrganizations] = useState<boolean>(false);
    const [orgSelectInputs, setOrgSelectInputs] = useState<SelectInputs>();    
    const [hiddenStatuses, setHiddenStatuses] = useState<string[]>([]);
    const [showPieChartCount, setShowPieChartCount] = useState(true);
    const [jobTitle, setJobTitle] = useState<string>("All Jobs");
    const [loading, setLoading] = useState<boolean>(false);    
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" }); 
    const [toErrorPage] = useErrorPage();
    const jobId = selectedJob === "All" ? 0 : selectedJob.jobId;
    const totalPages = Math.ceil(jobStats.length / pageSize);
    const scrollElementRef = useRef<any>();    
 
    const perPageSelectInputs: SelectInputs = {
        default: pageSize.toString(),
        options: [            
            {"value": 15, "option": "15"},
            {"value": 20, "option": "20"},
            {"value": 30, "option": "30"},
            {"value": 60, "option": "60"}
        ]
    }

    useEffect(() => {
        getOrganizations();
    }, []);

    useEffect(() => {     
        retrieveStatByJob();                        
        retrieveStatByStatus();   
        retrieveStatByDate(); 
        setShowPieChartCount(false);
    }, [jobType, orgId]);

    useEffect(() => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setPaginatedJobs(jobStats.slice(startIndex, endIndex));
    }, [page, pageSize, jobStats]);

    const jobSelectInputs: SelectInputs = {
        default: "All",
        options: [
            { value: "0", option: "All" },
            ...(jobList?.map((job: StatByJobModel) => ({
                value: job.jobId,
                option: job.jobTitle
            })))
        ]
    };

    const statusStatsData = statusStats?.map((status: ApplicationStatus) => ({
        name: status.status,
        value: status.applicationsCount
    }));

    const statusStatsColors = {
        colors: statusStats?.map((status: ApplicationStatus) => ({
            colorCode: status.colorCode,
            statusText: status.status
        }))
    };

    const dateStatsData = dateStats?.map((dateStats: StatByDateModel) => ({
        name: dateStats.dateStr,
        applications: dateStats.count
    }));

    const handlePageSize = (selectedPageSize: number) => {
        setPageSize(selectedPageSize);
    }

    const handlePagination = (selectedPage: number) => {
        setPage(selectedPage);
        scrollElementRef.current.scrollIntoView();       
    }

    const handleApplyclick = () => {
        retrieveStatByJob();    
        retrieveStatByStatus();   
        retrieveStatByDate();
        if (selectedJob !== "All") {
            setJobTitle(selectedJob.jobTitle);
        } else {
            setJobTitle("All Jobs");
        }      
    }

    const handleJobTypeChange = (selectedType: string) => {
        setJobType(selectedType);
        setHiddenStatuses([]);
        setPage(1);
    }

    const handleStatusItemClick = (statusName: string) => {
        setShowPieChartCount(false);
        setHiddenStatuses(prev => 
            prev.includes(statusName) 
            ? prev.filter(s => s !== statusName)
            : [...prev, statusName]
        );
    };

    const setOrganization = (selectedOrgId: number) => {
        if (orgSelectInputs) {
            const selectedOption = orgSelectInputs.options.find((opt) => opt.value === selectedOrgId);
            if (selectedOption) {
                setOrgName(selectedOption.option);
                setSelectedJob("All");
                setJobTitle("All Jobs");
                setStartDate(formatDate(oneMonthAgo));
                setEndDate(formatDate(today));
                setPage(1);
                setHiddenStatuses([]);
                setOrgId(selectedOrgId);
            }
        }
    };

    const getCVStatus = (type: string) => ({
        cv: type === "All" || type === "CV",
        cvLess: type === "All" || type === "CV Less"
    })

    async function getOrganizations() {        
        await getOrganizationDetails(recruiter!.recruiterId, recruiter!.organizationId, recruiter!.isOrganizationAdmin, recruiter!.token)
        .then((vacancyDetails: VacancyModel) => {
            setOrgName(vacancyDetails.organizationName);
            setHasSubOrganizations(vacancyDetails.hasSubOrganizations);            
            if(vacancyDetails.hasSubOrganizations) {
                const orgOptions = new Array<Option>();                 
                vacancyDetails.subOrganizations.forEach((organization) => {                           
                    let option: Option = {
                        value: organization.organizationId,
                        option: organization.organizationName
                    }           
                    orgOptions.push(option);
                });
                const tempSelectInputs = {
                    default: orgOptions[0].option,
                    options: orgOptions
                }              
                setOrgSelectInputs(tempSelectInputs);                
            }            
        })    
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);                
        });       
    }

    async function retrieveStatByJob() {
        let model: StatSearchCriteria = {
            jobId: jobId,
            recruiterId: recruiter!.recruiterId,
            organizationId: orgId,
            startDate: startDate,
            endDate: endDate,            
            ...getCVStatus(jobType)            
        }
        await getStatByJob(model, recruiter!.token)
        .then((response: StatByJobModel[]) => {
            if (jobId === 0) {
                setJobList(response);
            }
            setJobStats(response);
            const cvLessCount = response.filter(job => job.cvLess).length;
            const totalApps = response.reduce((sum, job) => sum + job.applicationCount, 0);
            const opened = response.reduce((sum, job) => sum + job.readCount, 0);
            const openRate = totalApps > 0 ? (opened / totalApps) * 100 : 0;
            setStatsCount(prev => ({
                ...prev,
                totalJobCount: response.length,
                cvLessJobCount: cvLessCount,
                cvBasedJobCount: response.length - cvLessCount,
                totalApplicationCount: totalApps,
                openedCount: opened,
                notOpenedCount: totalApps - opened,
                openedRate: openRate,
                notOpenedRate: totalApps > 0 ? 100 - openRate : 0
            }));       
            setJobStatLoading(false); 
        })    
        .catch((error) => {                  
            toErrorPage(error, UserType.Recruiter);
        });       
    }

    async function retrieveStatByDate() {        
        let model: StatSearchCriteria = {
            jobId: jobId,
            recruiterId: recruiter!.recruiterId,
            organizationId: orgId,
            startDate: startDate,
            endDate: endDate,                        
            ...getCVStatus(jobType),            
            groupBy: "none"
        }
        await getStatByDate(model, recruiter!.token)
        .then((response: StatByDateModel[]) => {
            setDateStats(response);
        })    
        .catch((error) => {                  
            toErrorPage(error);
        });       
    }

    async function retrieveStatByStatus() {  
        setLoading(true);        
        let model: StatSearchCriteria = {
            jobId: jobId,
            organizationId: orgId,
            recruiterId: recruiter!.recruiterId,
            startDate: startDate,
            endDate: endDate,
            ...getCVStatus(jobType)            
        }
        await getStatByStatus(model, recruiter!.token)
        .then((response: ApplicationStatus[]) => {
            setStatusStats(response);
            const filtered = response?.filter(({ applicationStatusId }) => applicationStatusId !== 18 && applicationStatusId !== 19);
            const totalFiltered = filtered.reduce((sum, { applicationsCount }) => sum + applicationsCount, 0);
            const openedFiltered = filtered.reduce((sum, { readApplicationsCount }) => sum + readApplicationsCount, 0);
            const openedFilteredRate = totalFiltered > 0 ? (openedFiltered / totalFiltered) * 100 : 0;
            setStatsCount(prev => ({
                ...prev,
                openedFilteredCount: openedFiltered,
                notOpenedFilteredCount: totalFiltered - openedFiltered,
                openedFilteredRate: openedFilteredRate,
                notOpenedFilteredRate: totalFiltered > 0 ? 100 - openedFilteredRate : 0
            }));
            setLoading(false); 
        })    
        .catch((error) => {                  
            toErrorPage(error, UserType.Recruiter);
        });       
    }

    async function downloadStatExcelSheet() {
        let model: StatSearchCriteria = {
            jobId: jobId,
            recruiterId: recruiter!.recruiterId,
            organizationId: orgId,
            startDate: startDate,
            endDate: endDate,                        
            ...getCVStatus(jobType),            
            groupBy: "none"
        }
        await downloadStatToExcel(model, recruiter!.token)
        .then((response: any) => { })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);            
        });
    }

    return (
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu/>
                </div>
                <div style={styles.rightWrapper}>
                    {!isMobile && <div style={styles.buttonWrapper}>
                        <div style={styles.backButton.wrapper}>
                            <div style={styles.backButton.container} onClick={() => navigate(-1)}>
                                <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                                <span style={styles.backButton.text}>{"Back"}</span>
                            </div>
                        </div>
                        <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                    </div>}
                    <div style={styles.statisticSection}>
                        <div style={styles.title}>{"Statistics"}</div>
                        {isMobile && <div style={styles.jobNameWrapper}>
                            <div>{orgName}</div>
                            <div>{jobTitle}</div>
                        </div>}
                        <div style={styles.filterPanel}>
                            <div style={styles.filterPanel.jobs}>
                                <SingleSelect
                                    key={orgId} 
                                    styles={styles.filterPanel.jobSelect}  
                                    label={"Jobs"} 
                                    selectInputs={jobSelectInputs} 
                                    getSelected={(value) => {
                                        if (value === "0" || value === 0) {
                                            setSelectedJob("All");
                                        } else {
                                            const selectedJob = jobList.find(job => job.jobId === Number(value));
                                            if(selectedJob) {
                                                setSelectedJob(selectedJob);   
                                            }
                                            else {
                                                setSelectedJob("All");
                                            }
                                        }
                                    }}
                                />                           
                            </div>
                            <div style={styles.filterPanel.date}>
                                <span style={styles.filterPanel.text}>{"From"}</span>                                     
                                <input
                                    type="date"
                                    id="fromDate"
                                    name="startDate"
                                    value={startDate}  
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={styles.filterPanel.dateInput}                                        
                                />                     
                            </div>
                            <div style={styles.filterPanel.date}>
                                <span style={styles.filterPanel.text}>{"To"}</span>                                     
                                <input
                                    type="date"
                                    id="toDate"
                                    name="endDate"
                                    value={endDate}  
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={styles.filterPanel.dateInput}                                        
                                />                     
                            </div>
                            <Button style={styles.filterPanel.applyButton} onClick={handleApplyclick}>{"Apply"}</Button>
                        </div>
                    </div>
                    {hasSubOrganizations && orgSelectInputs && (
                        <div style={styles.selectWrapper}>
                            <SingleSelect styles={styles.orgSelect} label={"Organizations"} selectInputs={orgSelectInputs!} getSelected={setOrganization} />
                        </div>
                    )}
                    {!isMobile && <div style={styles.jobNameWrapper}>
                        {orgName! && <div style={styles.jobNameWrapper.subOrganization}>{orgName + " -"}</div>}
                        <div style={orgName! && styles.jobNameWrapper.jobName}>{jobTitle}</div>
                    </div>}
                    <div style={styles.jobCategoryWrapper}>
                        {loading ? <VacancyCountLoader/> :
                            <div style={styles.jobCategory}>
                                <div 
                                    style={{...styles.jobCategory.tag, ...(jobType === "All" && styles.jobCategory.activeTag)}} 
                                    onClick={() => handleJobTypeChange ("All")}
                                >
                                    All Jobs {jobType === "All" && statsCount.totalJobCount > 0 && `(${statsCount.totalJobCount})`}
                                </div>
                                <div 
                                    style={{...styles.jobCategory.tag, ...(jobType === "CV Less" && styles.jobCategory.activeTag)}} 
                                    onClick={() => handleJobTypeChange ("CV Less")}
                                >
                                    CV Less {statsCount.cvLessJobCount > 0 && `(${statsCount.cvLessJobCount})`}
                                </div>
                                <div 
                                    style={{...styles.jobCategory.tag, ...(jobType === "CV" && styles.jobCategory.activeTag)}} 
                                    onClick={() => handleJobTypeChange ("CV")}
                                >
                                    CV {statsCount.cvBasedJobCount > 0 && `(${statsCount.cvBasedJobCount})`}
                                </div>
                            </div>
                        }
                        <div style={styles.exportExcel.wrapper}>
                            <div style={styles.exportExcel.button} onClick={downloadStatExcelSheet}>
                                <Excel style={styles.exportExcel.icon}/>
                                {"Export as Excel"}
                            </div>
                        </div>
                    </div>
                    {loading ? <StatisticsLoader/> :
                        <div>
                            <div style={styles.applicationsSummary}>
                                <div style={styles.applicationsSummary.totalApplications}>
                                    <div style={{...styles.applicationsSummary.totalApplications.count, ...styles.font}}>{loading ? "-" : statsCount.totalApplicationCount}</div>
                                    <div style={{...styles.applicationsSummary.totalApplications.text, ...styles.font}}>{"Total Applications"}</div>
                                </div>
                                <div style={styles.applicationsSummary.openRateWrapper}>
                                    <div style={styles.applicationsSummary.openRate}>
                                        <div>
                                            <div style={{...styles.applicationsSummary.openRate.value, ...styles.font}}>{loading ? "-" : statsCount.openedRate.toFixed(2) + "%"}</div>
                                            <div style={{...styles.applicationsSummary.openRate.text, ...styles.font}}>{"Open Rate"}</div>
                                            <div style={{...styles.applicationsSummary.openRate.totalText, ...styles.font}}>{"of Total Applications"}</div>
                                        </div>
                                        <div style={styles.applicationsSummary.openRate.chart}>
                                            <PieChartComponet 
                                                chartColors={styles.applicationsSummary.openRate.chartColors}
                                                chartData={[
                                                    { name: 'Opened', value: statsCount.openedRate },
                                                    { name: 'Not Opened', value: statsCount.notOpenedRate }
                                                ]}
                                                style = {styles.pieChartComponet}
                                            />
                                        </div>
                                        <div style={styles.applicationsSummary.openRate.status}>
                                            <div style={styles.applicationsSummary.openRate.openedWrapper}>
                                                <div style={{...styles.applicationsSummary.openRate.dot, background: Color.HarvestGold}}></div>
                                                <span style={styles.applicationsSummary.openRate.statusText}>{`Opened CV (${statsCount.openedCount})`}</span>
                                            </div>
                                            <div style={styles.applicationsSummary.openRate.notOpenedWrapper}>
                                                <div style={{...styles.applicationsSummary.openRate.dot, background: Color.MutedGold}}></div>
                                                <span style={styles.applicationsSummary.openRate.statusText}>{`Not Opened CV (${statsCount.notOpenedCount})`}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={styles.applicationsSummary.filteredWrapper}>
                                        <div>
                                            <div style={{...styles.applicationsSummary.filteredWrapper.value, ...styles.font}}>{loading ? "-" : statsCount.openedFilteredRate.toFixed(2) + "%"}</div>
                                            <div style={{...styles.applicationsSummary.filteredWrapper.text, ...styles.font}}>{"Open Rate"}</div>
                                            <div style={{...styles.applicationsSummary.filteredWrapper.filteredText, ...styles.font}}>{"of Filtered Applications"}</div>
                                        </div>
                                        <div style={styles.applicationsSummary.filteredWrapper.chart}>
                                            <PieChartComponet 
                                                chartColors={styles.applicationsSummary.filteredWrapper.chartColors}
                                                chartData={[
                                                    { name: 'Opened', value: statsCount.openedFilteredRate},
                                                    { name: 'Not Opened', value: statsCount.notOpenedFilteredRate}
                                                ]}
                                                style = {styles.pieChartComponet}
                                            />
                                        </div>
                                        <div style={styles.applicationsSummary.filteredWrapper.status}>
                                            <div style={styles.applicationsSummary.filteredWrapper.openedWrapper}>
                                                <div style={{...styles.applicationsSummary.filteredWrapper.dot, background: Color.Green}}></div>
                                                <span style={styles.applicationsSummary.filteredWrapper.statusText}>{`Opened CV (${statsCount.openedFilteredCount})`}</span>
                                            </div>
                                            <div style={styles.applicationsSummary.filteredWrapper.notOpenedWrapper}>
                                                <div style={{...styles.applicationsSummary.filteredWrapper.dot, background: Color.FreshGreen}}></div>
                                                <span style={styles.applicationsSummary.filteredWrapper.statusText}>{`Not Opened CV (${statsCount.notOpenedFilteredCount})`}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={styles.applicationStatusWrapper}>
                                <div style={styles.statusWrapper}>
                                    <div style={{...styles.statusWrapper.title, ...styles.font}}>{"Application Status"}</div>
                                    <div style={styles.statusWrapper.chart}>
                                        <PieChartComponet 
                                            chartColors={statusStatsColors}
                                            chartData={statusStatsData}
                                            displayCount={showPieChartCount}
                                            isShowStatus={true}
                                            style = {styles.pieChartComponet}
                                            hiddenStatuses={hiddenStatuses}
                                            onChartClick={() => setShowPieChartCount(true)}
                                        />
                                    </div>
                                    <div style={styles.statusWrapper.statusList}>
                                        {statusStats?.map((status, index: number) => {
                                            const isHidden = hiddenStatuses.includes(status.status);
                                            return (
                                                <div key={index} style={styles.statusWrapper.status} onClick={() => handleStatusItemClick(status.status)}>
                                                    <div style={{...styles.statusWrapper.status.color, backgroundColor: isHidden ? Color.MiddleGray : status.colorCode}} />
                                                    <div style={{...styles.statusWrapper.status.name, textDecoration: isHidden ? "line-through" : "none"}}>
                                                        {status.status}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div style={styles.applicationsByDate}>
                                    <div style={{...styles.applicationsByDate.title, ...styles.font}}>{"Applications by Date"}</div>
                                    <div style={styles.applicationsByDate.chartWrapper}>
                                        <div style={styles.applicationsByDate.chart}>
                                            <ResponsiveContainer>
                                                <BarChart data={dateStatsData} style={styles.applicationsByDate.barChart}>
                                                    <XAxis dataKey="name" tick={{fontSize: isMobile ? 14 : 12}} padding={{left: 7}}/>
                                                    <YAxis tick={{fontSize: isMobile ? 14 : 12}}/>
                                                    <YAxis/>
                                                    <Tooltip
                                                        cursor={{ fill: 'transparent' }}
                                                        content={({ payload, label }) => {
                                                            if (!payload || payload.length === 0 || payload[0].value === 0) return null;
                                                            return (
                                                                <div style={styles.applicationsByDate.toolTip}>
                                                                    <div style={styles.applicationsByDate.toolTip.lable}>{label}</div>
                                                                    <div style={styles.applicationsByDate.toolTip.count}>Applications: {payload[0].value}</div>
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Bar dataKey="applications" fill={Color.Blue} barSize={20}/>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    <div style={styles.jobsStatisticsWrapper} ref={scrollElementRef}> 
                        <div style={{...styles.jobsStatisticsWrapper.title, ...styles.font}}>{"Jobs Statistics"}</div>
                        <div style={styles.jobsStatisticsWrapper.selectWrapper}>
                            <div style={styles.jobsStatisticsWrapper.selectRow}>
                                {isMobile && <SingleSelect styles={styles.jobsStatisticsWrapper.perPageSelect} label={"Post Per Page"} selectInputs={perPageSelectInputs} getSelected={setPageSize} />}
                            </div>
                        </div>
                    </div>
                    <div>
                        {jobStatLoading ? <VacancyListLoader/> :
                            <>
                                {paginatedJobs.length > 0 ? <>
                                    {paginatedJobs.map((vacancy, index) =>
                                        <div key={index} style={styles.vacancyCard}>
                                            <div style={styles.vacancyCard.jobTitleWrapper}>
                                                <div style={styles.vacancyCard.jobTitle}>{vacancy.jobTitle}</div>
                                                {isMobile && <div style={{
                                                    ...styles.vacancyCard.jobType,
                                                    background: vacancy.cvLess ? Color.LightBlue : Color.Nyanza,
                                                    color: vacancy.cvLess ? Color.Blue : Color.Green
                                                }}>
                                                    {vacancy.cvLess ? "CV Less" : "CV"}
                                                </div>}
                                            </div>
                                            <div style={styles.vacancyCard.jobId}>{"Job ID "}<span style={styles.vacancyCard.jobId.bold}>{vacancy.jobId}</span></div>
                                            {!isMobile && <div style={{
                                                ...styles.vacancyCard.jobType,
                                                background: vacancy.cvLess ? Color.LightBlue : Color.Nyanza,
                                                color: vacancy.cvLess ? Color.Blue : Color.Green
                                            }}>
                                                {vacancy.cvLess ? "CV Less" : "CV"}
                                            </div>}
                                            <div style={styles.vacancyCard.dateWrapper}>
                                                <div style={styles.vacancyCard.createdText}><Calendar style={styles.vacancyCard.calendarIcon} />{"Created"}</div>
                                                <div style={styles.vacancyCard.date}>{formatDateWithSuperscript(new Date(vacancy.createdDate))}</div>
                                            </div>
                                            <div style={styles.vacancyCard.applicationDetails}>
                                                <div style={styles.vacancyCard.totalApplication}>
                                                    <div style={styles.vacancyCard.applicationCount}>{vacancy.applicationCount}</div>
                                                    <div style={styles.vacancyCard.applicationText}>{"Total Applications"}</div>
                                                </div>
                                                <div style={styles.vacancyCard.openApplications}>
                                                    <div style={styles.vacancyCard.openCount}>{vacancy.readCount}</div>
                                                    <div style={styles.vacancyCard.openText}>{"Open Applications"}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div style={styles.paginationWrapper}>
                                        {!isMobile && <SingleSelect styles={styles.perPageSelect} label={"Posts Per Page"} selectInputs={perPageSelectInputs} getSelected={handlePageSize} />}
                                        {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination} extendedStyles={styles.pagination} />}
                                    </div>    
                                </> 
                                : <FeedbackCard
                                    styles={styles.noVacancies}
                                    message={`There are no jobs available`}
                                />} 
                            </>
                        }
                    </div>
                </div> 
            </div>
            <Footer/>
        </>
    )
}